import endpoints from "../../../../utils/endpoints";
import { ENQUIRY_SOURCE } from "../../../../constants/enquirySource";
import type { FlowStep, StepRuntime } from "../types";
import { FlowStepError } from "../engine/errors";
import { garaazTransitionPath, requireEnquiry, verifyEq } from "./common";

export const STEP_CREATE_ENQUIRY = "create_enquiry";
export const STEP_ACK_ENQUIRY = "ack_enquiry";
export const STEP_PART_CONVERSION_START = "part_conversion_start";
export const STEP_PART_CONVERSION_END = "part_conversion_end";

const isBakedEnquiry = (order: any) =>
  Array.isArray(order?.orderItems) && order.orderItems.length > 0;

/** Enquiry statuses once it is acknowledged (part conversion states sit after ACK). */
const ENQUIRY_ACKED_STATUSES = [
  "ACK",
  "PART_CONVERSION_STARTED",
  "PART_CONVERSION_COMPLETED",
];

/**
 * Same two-call rule as the OrCA footer (partConversionCommands): the
 * garaaz_transition is best effort (enquiries started via meta-info before the
 * states existed stay in ACK), the meta-info PATCH always runs because it fires
 * the Estimate Ready push and the IAM part confirmation.
 */
const tryPartConversionTransition = async (
  rt: StepRuntime,
  orderId: string,
  action: "START_PART_CONVERSION" | "COMPLETE_PART_CONVERSION"
) => {
  try {
    await rt.api({
      label: action === "START_PART_CONVERSION" ? "Start part conversion (state)" : "Complete part conversion (state)",
      method: "POST",
      path: garaazTransitionPath(orderId, action),
      body: {},
    });
  } catch (error: any) {
    rt.log(`${action} transition rejected, continuing with meta-info: ${error?.message || error}`, "warn");
  }
};

export const createEnquiryStep: FlowStep = {
  id: STEP_CREATE_ENQUIRY,
  name: "Place enquiry",
  targetLabel: "Enquiry placed",
  description:
    "Customer places an enquiry (POST /v1/orders without rootOrderId). A baked enquiry (at least one part) is auto-ACK'd and part conversion is stamped start+end, which sends part confirmation to the IAM supplier.",
  actor: "CUSTOMER",
  kind: "api",
  estimateMs: [1500, 4000],
  execute: async (rt) => {
    const { config, orcaUser } = rt.ctx;
    if (!config.customerId) throw new FlowStepError("No customer selected.");
    if (!config.parts.length) throw new FlowStepError("Add at least one part to the configuration.");
    if (!config.shipmentAddress) {
      throw new FlowStepError("No shipment address configured for the customer.");
    }

    const payload = {
      customerId: config.customerId,
      initiatorId: orcaUser._id,
      customerRemarks: config.remarks || undefined,
      shipmentAddress: config.shipmentAddress,
      billingDetails: config.billingDetails || undefined,
      orderDate: new Date().toISOString(),
      orderItems: config.parts.map((p) => ({
        partId: p.partId,
        orderedQuantity: p.quantity,
      })),
      orderMedia: config.orderMediaIds.length ? config.orderMediaIds : undefined,
      carMaker: config.carMaker || undefined,
      isTestOrder: config.isTestCustomer !== false,
      source: ENQUIRY_SOURCE.WEB_ORCA,
    };

    const res = await rt.api<any>({
      label: "Place enquiry",
      method: "POST",
      path: endpoints.placeGaraazOrder,
      body: payload,
      actor: "CUSTOMER",
    });
    const order = res.data?.order ?? res.data;
    if (!order?._id || !order?.orderNo) {
      throw new FlowStepError("Enquiry response did not include an order id/number.", {
        apiCall: res.record,
      });
    }
    if (config.isTestCustomer !== false && order.isTestOrder !== true) {
      throw new FlowStepError(
        "Safety stop: the created order is NOT flagged isTestOrder. Aborting so no production flow continues.",
        { expected: "isTestOrder = true", actual: `isTestOrder = ${order.isTestOrder}`, apiCall: res.record }
      );
    }
    rt.ctx.enquiry = { orderId: String(order._id), orderNo: order.orderNo };
    const baked = isBakedEnquiry(order) || Boolean(rt.ctx.config.parts.length);
    if (baked && (ENQUIRY_ACKED_STATUSES.includes(order.orderStatus) || order.metaInfo?.partConversionEndTime)) {
      rt.log(
        `Baked enquiry: backend auto-ACK'd and completed part conversion (part confirmation is sent asynchronously)`,
        "info"
      );
    }
    rt.log(
      `Enquiry ${order.orderNo} created (${order.orderStatus}) enquiryNo=${order.orderNo} _id=${order._id}`,
      "success"
    );
  },
  verify: (s) =>
    verifyEq(
      "enquiry.orderStatus",
      ["NEW", ...ENQUIRY_ACKED_STATUSES],
      s.enquiry?.orderStatus
    ),
};

export const ackEnquiryStep: FlowStep = {
  id: STEP_ACK_ENQUIRY,
  name: "ACK enquiry",
  targetLabel: "Enquiry ACK",
  description:
    "OrCA acknowledges the enquiry (POST /v1/orders/:id/garaaz_transition?action=ACK). Baked enquiries are already ACK'd on create — this step records that as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2500],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    const status = rt.ctx.snapshot?.enquiry?.orderStatus;
    if (status && status !== "NEW") {
      rt.log(`Enquiry already ${status} (backend auto-ACK on baked create)`, "info");
      return;
    }
    await rt.api({
      label: "ACK enquiry",
      method: "POST",
      path: garaazTransitionPath(enquiry.orderId, "ACK"),
      body: {},
    });
  },
  verify: (s) =>
    verifyEq("enquiry.orderStatus", [...ENQUIRY_ACKED_STATUSES, "ALLOCATED", "BLOCKED"], s.enquiry?.orderStatus),
};

export const partConversionStartStep: FlowStep = {
  id: STEP_PART_CONVERSION_START,
  name: "Start part conversion",
  targetLabel: "Part conversion started",
  description:
    "OrCA starts part conversion (garaaz_transition?action=START_PART_CONVERSION → PART_CONVERSION_STARTED, then PATCH meta-info partConversionStartTime). Baked enquiries already have this done — recorded as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    if (rt.ctx.snapshot?.enquiry?.partConversion !== "NOT_STARTED") {
      rt.log("Part conversion already started (backend stamped it on baked create)", "info");
      return;
    }
    await tryPartConversionTransition(rt, enquiry.orderId, "START_PART_CONVERSION");
    await rt.api({
      label: "Start part conversion",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(enquiry.orderId),
      body: { partConversionStartTime: new Date().toISOString() },
    });
  },
  verify: (s) =>
    verifyEq("enquiry.partConversion", ["IN_PROGRESS", "COMPLETED"], s.enquiry?.partConversion),
};

export const partConversionEndStep: FlowStep = {
  id: STEP_PART_CONVERSION_END,
  name: "Complete part conversion",
  targetLabel: "Part conversion completed",
  description:
    "OrCA completes part conversion (garaaz_transition?action=COMPLETE_PART_CONVERSION → PART_CONVERSION_COMPLETED, then PATCH meta-info partConversionEndTime, which triggers IAM part confirmation). Baked enquiries already have this done — recorded as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    if (rt.ctx.snapshot?.enquiry?.partConversion === "COMPLETED") {
      rt.log("Part conversion already completed (backend stamped it on baked create)", "info");
      rt.markEventProcessed();
      return;
    }
    await tryPartConversionTransition(rt, enquiry.orderId, "COMPLETE_PART_CONVERSION");
    await rt.api({
      label: "Complete part conversion",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(enquiry.orderId),
      body: { partConversionEndTime: new Date().toISOString() },
    });
    rt.markEventProcessed();
  },
  verify: (s) => verifyEq("enquiry.partConversion", "COMPLETED", s.enquiry?.partConversion),
};
